/**
 * Route table and the app shell.
 *
 * Two surfaces, and the split at the top of `renderRoute` is load-bearing:
 * `/r/*` is the reviewer's, and it renders **before the auth gate** because a
 * reviewer has no Mauvely account and must never be sent to a sign-in page.
 * Cloud does the same for `/s/*`. Everything else is the author's.
 */

import { useEffect, useRef } from 'react';
import { useRouter } from './router';
import { AppProvider, useApp } from './state';
import { Wordmark } from './components/Wordmark';
import { Project } from './pages/Project';
import { ProjectList } from './pages/ProjectList';
import { Reviewer as ReviewerPage } from './pages/Reviewer';
import { RoundsPage } from './pages/Rounds';
import { display, mono } from './theme';

/**
 * Which theme each surface wears. Set on the root element rather than chosen by
 * the reader — see theme.ts for why Review has no toggle.
 */
function useSurfaceTheme(surface: 'author' | 'reviewer') {
  useEffect(() => {
    document.documentElement.setAttribute(
      'data-theme',
      surface === 'author' ? 'dark' : 'light'
    );
  }, [surface]);
}

function Centered({ children }: { children: React.ReactNode }) {
  return (
    <div
      className="mv-app"
      style={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 14,
        padding: 24,
        // The app ground, not the site's: these are the app's own windows with
        // one message in them, and the ground has to be the thing that does not
        // change when the session lands.
        background: 'var(--app-ground)',
        textAlign: 'center'
      }}
    >
      {children}
    </div>
  );
}

/**
 * The route cross-fade.
 *
 * A wrapper that replays a CSS animation, not a `key` on the outlet. Keying
 * would remount the surface under it, and the workspace re-mounting means a new
 * course iframe and a new socket on every navigation. Removing the class,
 * reading a layout property to flush the removal, and adding it back is the
 * only way to restart an animation on an element that stays where it is.
 *
 * `prefers-reduced-motion` turns `.mv-page` into a fade with no rise, in
 * styles.css. It is deliberately not turned off: a fade with no movement is
 * what the readme asks for, and it is the only thing that says the view changed
 * when the chrome around it did not.
 */
function PageFade({ path, children }: { path: string; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.classList.remove('mv-page');
    void el.offsetWidth;
    el.classList.add('mv-page');
  }, [path]);

  return (
    <div ref={ref} className="mv-page">
      {children}
    </div>
  );
}

function SignedOut() {
  useSurfaceTheme('author');
  useEffect(() => {
    // Round-trips through the main site, which owns sessions. It lands back here
    // because `review.mauvely.com` is in `RETURN_HOSTS` in website-main's
    // shared/returnto.ts — without that entry this ends on /account instead.
    const next = encodeURIComponent(window.location.href);
    // The staging mirror signs in on its own domain.
    const account = window.location.hostname.endsWith('.mauvely.dev') ? 'https://mauvely.dev' : 'https://mauvely.com';
    window.location.href = `${account}/signin?next=${next}`;
  }, []);
  return (
    <Centered>
      <Wordmark reversed />
      <p style={mono}>Taking you to sign in</p>
    </Centered>
  );
}

function Unreachable() {
  useSurfaceTheme('author');
  const { refresh } = useApp();
  return (
    <Centered>
      <Wordmark reversed />
      <h1 style={display(28)}>Review is not answering.</h1>
      <p style={{ color: 'var(--text-body)', maxWidth: 420 }}>
        Your session is fine — something between here and the server is not. Try again in a
        moment.
      </p>
      <button type="button" onClick={() => void refresh()}>
        Try again
      </button>
    </Centered>
  );
}

/**
 * The deployment is incomplete.
 *
 * Separate from `Unreachable` on purpose. The two look identical to a user and
 * mean opposite things — one resolves itself and one needs someone to go and
 * finish a deploy — and collapsing them is what made a missing route look like a
 * network fault.
 */
function NotReadyScreen() {
  useSurfaceTheme('author');
  const { notReady, refresh } = useApp();
  const missing = [
    ...(notReady?.missingTables ?? []),
    ...(notReady?.missingColumns ?? [])
  ];

  return (
    <Centered>
      <Wordmark reversed />
      <h1 style={display(28)}>Review is not set up yet.</h1>
      <p style={{ color: 'var(--text-body)', maxWidth: 460 }}>
        {notReady?.message ?? 'This deployment is missing something it needs.'}
      </p>

      {missing.length > 0 ? (
        <p style={{ ...mono, maxWidth: 460, lineHeight: 1.7 }}>
          missing · {missing.join(' · ')}
        </p>
      ) : null}

      {notReady?.storage === false ? (
        <p style={mono}>no r2 binding · imports and course serving are off</p>
      ) : null}

      {notReady?.hint ? (
        <p style={{ color: 'var(--text-muted)', maxWidth: 460 }}>{notReady.hint}</p>
      ) : null}

      <button type="button" onClick={() => void refresh()}>
        Check again
      </button>
    </Centered>
  );
}

/**
 * The reviewer surface.
 *
 * `/r/<slug>/<token>` — the token is the credential, so it is in the path and
 * never sent anywhere but this app's own API. `referrer-policy: no-referrer` in
 * worker/lib/headers.ts is what keeps it from following someone to another site.
 */
function Reviewer({ path }: { path: string }) {
  useSurfaceTheme('reviewer');
  const m = /^\/r\/([^/]+)\/([^/]+)$/.exec(path);
  if (!m) {
    return (
      <Centered>
        <Wordmark />
        <h1 style={display(28)}>That link is no longer available.</h1>
        <p style={{ color: 'var(--text-body)', maxWidth: 420 }}>
          Ask whoever sent it for a new one.
        </p>
      </Centered>
    );
  }
  return <ReviewerPage slug={decodeURIComponent(m[1]!)} token={decodeURIComponent(m[2]!)} />;
}

function Routed() {
  const { path } = useRouter();

  return <PageFade path={path}>{surfaceFor(path)}</PageFade>;
}

/** Which surface a path is. Split out of `Routed` so the cross-fade above wraps
 *  one element rather than being repeated on eight early returns. */
function surfaceFor(path: string) {
  // Before the gate, deliberately. A reviewer has no account to sign in to.
  if (path.startsWith('/r/')) return <Reviewer path={path} />;
  return <Gated path={path} />;
}

function Gated({ path }: { path: string }) {
  const { status } = useApp();

  if (status === 'loading') return <Centered>{null}</Centered>;
  if (status === 'notReady') return <NotReadyScreen />;
  if (status === 'unreachable') return <Unreachable />;
  if (status === 'signedOut' || status === 'secondFactor') return <SignedOut />;

  const rounds = /^\/p\/([A-Za-z0-9_-]+)\/rounds$/.exec(path);
  if (rounds) return <RoundsSurface id={rounds[1]!} />;

  const project = /^\/p\/([A-Za-z0-9_-]+)$/.exec(path);
  if (project) return <ProjectSurface id={project[1]!} />;

  return <ProjectListSurface />;
}

function RoundsSurface({ id }: { id: string }) {
  useSurfaceTheme('author');
  return <RoundsPage id={id} />;
}

function ProjectSurface({ id }: { id: string }) {
  useSurfaceTheme('author');
  return <Project id={id} />;
}

function ProjectListSurface() {
  useSurfaceTheme('author');
  return <ProjectList />;
}

export default function App() {
  return (
    <AppProvider>
      <Routed />
    </AppProvider>
  );
}
